import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Button,
  Alert,
  useTheme,
  useMediaQuery,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import '../../styles/section-divider.css';
import FolderIcon from '@mui/icons-material/Folder';
import { CodeIcon } from '../icons';
import ChatIcon from '@mui/icons-material/Chat';
import DescriptionIcon from '@mui/icons-material/Description';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import SecurityIcon from '@mui/icons-material/Security';
import ApiIcon from '@mui/icons-material/Api';
import FeaturedPlayListIcon from '@mui/icons-material/FeaturedPlayList';
import PersonIcon from '@mui/icons-material/Person';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import GroupsIcon from '@mui/icons-material/Groups';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import HandshakeIcon from '@mui/icons-material/Handshake';
import SearchIcon from '@mui/icons-material/Search';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CreateIcon from '@mui/icons-material/Create';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ArticleIcon from '@mui/icons-material/Article';
import VerifiedIcon from '@mui/icons-material/Verified';
import RateReviewIcon from '@mui/icons-material/RateReview';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import useSection from '../../hooks/useSection';
import useAuth from '../../hooks/useAuth';
import sectionColorService from '../../services/sectionColorService';
import SectionOrchestratorSelector from './SectionOrchestratorSelector';
import GitHubIcon from '@mui/icons-material/GitHub';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import codeAwareConversationService from '../../services/codeAwareConversationService';

/**
 * Improved SectionedTaskSelector component that matches Foundation exactly
 * 
 * @param {Object} props - Component props
 * @param {Array} props.selectedTasks - Array of task IDs that are currently selected
 * @param {Function} props.onTaskSelection - Callback function when a task is selected/deselected
 * @param {Function} props.onVisibleTasksChange - Callback function to pass back visible task IDs
 * @param {Function} props.onSave - Callback function when the save button is clicked
 * @param {Function} props.onContinue - Callback function when the continue button is clicked
 * @param {boolean} props.showSaveButton - Whether to show the save button (default: true)
 * @param {boolean} props.showSkipButton - Whether to show the skip button (default: true)
 * @param {boolean} props.showContinueButton - Whether to show the continue button (default: false)
 * @param {string} props.saveButtonText - Text for the save button (default: "Activate Selected Topics")
 * @param {string} props.continueButtonText - Text for the continue button (default: "Continue to Repository Setup")
 * @param {string} props.mode - Display mode: 'selection' or 'display' (default: 'selection')
 * @param {boolean} props.hideEmptySections - Whether to hide empty sections (default: true)
 * @param {boolean} props.isDialog - Whether this component is rendered in a dialog (default: false)
 * @param {string} props.title - Optional title for the component
 * @param {string} props.projectId - ID of the project (when available)
 * @returns {React.ReactElement} Component
 */
const SectionedTaskSelectorImproved = ({
  selectedTasks = [],
  onTaskSelection,
  onSelectAll,
  allTaskIds = [],
  onSave,
  onContinue,
  onSkip,
  onVisibleTasksChange, // New prop to pass back visible tasks count
  showSaveButton = true,
  showSkipButton = true,
  showContinueButton = false,
  saveButtonText = "Activate selected topics",
  continueButtonText = "Continue",
  mode = 'selection',
  hideEmptySections = true,
  isDialog = false,
  title,
  existingProjectTasks = [], // New prop to receive tasks that are already in the project
  projectId = null, // Project ID for orchestrator association
  applicationType = 'website' // Application type for filtering sections
}) => {
  const { sections, isLoadingSections, sectionsError, refetchSections } = useSection({ activeOnly: true });
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAllSections, setShowAllSections] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [continueLoading, setContinueLoading] = useState(false);
  const [isWorkspaceValid, setIsWorkspaceValid] = useState(false);
  const [generatingTasks, setGeneratingTasks] = useState(new Set());
  const [generationResults, setGenerationResults] = useState({});

  // Helper function to map document types to icon names
  const getIconByDocType = (docType) => {
    const iconMapping = {
      'PRD': 'DescriptionIcon',
      'APD': 'DescriptionIcon',
      'FIELD_VALIDATION': 'ListAltIcon',
      'DEV_GUIDELINES': 'CodeIcon',
      'TEST_CASE': 'ListAltIcon',
      'DESIGN_GUIDELINES': 'DesignServicesIcon',
      'API_GUIDELINES': 'ApiIcon',
      'TECH_STACK': 'SettingsIcon',
      'FLOWS': 'AccountTreeIcon',
      'SECURITY_GUIDELINES': 'SecurityIcon',
      'FEATURES': 'FeaturedPlayListIcon',
      'USER_STORIES': 'PersonIcon',
      'FINAL_PRD': 'DescriptionIcon',
      'MARKDOWN_FILE': 'DescriptionIcon',
      'VALIDATION': 'ListAltIcon',
      'WIREFRAMES': 'DesignServicesIcon',
      'USER_ROLES': 'GroupsIcon',
      'PROJECT_INFO': 'ChatIcon'
    };

    return iconMapping[docType] || 'FolderIcon';
  };

  // Use the workflowTiles as a fallback if no sections are available
  // Organized into categories matching our section structure
  const workflowCategories = {
    'Functional analysis': [
      { id: 'project-info', title: 'Project Info', icon: 'ChatIcon' },
      { id: 'features', title: 'Features', icon: 'FeaturedPlayListIcon' },
      { id: 'user-roles', title: 'User Roles', icon: 'GroupsIcon' },
      { id: 'user-stories', title: 'User Stories', icon: 'PersonIcon' },
      { id: 'flow', title: 'Process Flows', icon: 'AccountTreeIcon' },
    ],
    'Design': [
      { id: 'wireframes', title: 'Wireframes', icon: 'DesignServicesIcon' },
      { id: 'design-guidelines', title: 'Design Guidelines', icon: 'DesignServicesIcon' },
      { id: 'designs', title: 'Designs', icon: 'DesignServicesIcon' },
    ],
    'Technical': [
      { id: 'field-validations', title: 'Field Validations', icon: 'ListAltIcon' },
      { id: 'tech-stack', title: 'Technology Stack', icon: 'SettingsIcon' },
      { id: 'architecture', title: 'Architecture', icon: 'ArchitectureIcon' },
      { id: 'dev-guidelines', title: 'Development Guidelines', icon: 'CodeIcon' },
      { id: 'security-guidelines', title: 'Security Guidelines', icon: 'SecurityIcon' },
    ],
    'Testing': [
      { id: 'test-cases', title: 'Test Cases', icon: 'ListAltIcon' },
    ],
    'Infrastructure': [
      { id: 'version-control', title: 'Version Control', icon: 'HistoryIcon' },
      { id: 'cicd-pipeline', title: 'CI/CD Pipeline', icon: 'SettingsEthernetIcon' },
      { id: 'deployment', title: 'Deployment', icon: 'PublishIcon' },
    ],
  };

  // Flatten the categories for backwards compatibility
  const workflowTiles = Object.values(workflowCategories).flat();

  // Helper function to get icon component by name
  const getIconComponent = (iconName) => {
    const icons = {
      'CodeIcon': CodeIcon,
      'ChatIcon': ChatIcon,
      'DescriptionIcon': DescriptionIcon,
      'ListAltIcon': ListAltIcon,
      'SettingsIcon': SettingsIcon,
      'DesignServicesIcon': DesignServicesIcon,
      'SecurityIcon': SecurityIcon,
      'ApiIcon': ApiIcon,
      'FeaturedPlayListIcon': FeaturedPlayListIcon,
      'PersonIcon': PersonIcon,
      'ArchitectureIcon': ArchitectureIcon,
      'AccountTreeIcon': AccountTreeIcon,
      'GroupsIcon': GroupsIcon,
      'FolderIcon': FolderIcon,
      'GitHubIcon': GitHubIcon
    };

    const IconComponent = icons[iconName] || FolderIcon;
    return <IconComponent />;
  };

  // Handle task selection
  const handleTaskSelection = (taskId) => {
    console.log('SectionedTaskSelector - handleTaskSelection called with:', taskId);
    console.log('SectionedTaskSelector - current selectedTasks prop:', selectedTasks);
    
    // Call the parent callback directly to manage state
    if (onTaskSelection) {
      console.log('SectionedTaskSelector - calling parent onTaskSelection');
      onTaskSelection(taskId);
    } else {
      console.log('SectionedTaskSelector - no onTaskSelection callback');
    }
  };

  // Handle save button click
  const handleSave = async () => {
    try {
      setLoading(true);
      if (onSave) {
        await onSave(selectedTasks);
      }
    } catch (err) {
      setError(err.message || 'Failed to save selected tasks');
    } finally {
      setLoading(false);
    }
  };

  // Handle continue button click
  const handleContinue = async () => {
    try {
      setContinueLoading(true);
      // First save the selected tasks
      if (onSave) {
        await onSave(selectedTasks);
      }
      // Then continue to repository setup
      if (onContinue) {
        onContinue();
      }
    } catch (err) {
      setError(err.message || 'Failed to proceed to repository setup');
    } finally {
      setContinueLoading(false);
    }
  };

  // Handle skip button click
  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    }
  };

  // Validate project workspace on component mount
  useEffect(() => {
    const validateWorkspace = async () => {
      if (projectId) {
        try {
          const result = await codeAwareConversationService.validateProjectWorkspace(projectId);
          setIsWorkspaceValid(result.data.isValid);
        } catch (error) {
          console.error('Failed to validate workspace:', error);
          setIsWorkspaceValid(false);
        }
      }
    };

    validateWorkspace();
  }, [projectId]);

  // Handle task generation using Claude SDK
  const handleGenerateTask = async (taskId) => {
    if (!projectId || !isWorkspaceValid) {
      console.warn('Cannot generate task: no project ID or invalid workspace');
      return;
    }

    try {
      setGeneratingTasks(prev => new Set([...prev, taskId]));
      setError(null);

      await codeAwareConversationService.generateTaskConversationWithCallbacks(
        projectId,
        taskId,
        {
          onMessage: (message) => {
            console.log('Generation message:', message);
            // Handle real-time messages if needed
          },
          onError: (error) => {
            console.error('Generation error:', error);
            setError(`Failed to generate task ${taskId}: ${error.message}`);
          },
          onComplete: (messages) => {
            console.log('Generation complete for task:', taskId);
            setGenerationResults(prev => ({
              ...prev,
              [taskId]: {
                status: 'completed',
                messages,
                timestamp: new Date()
              }
            }));
          }
        }
      );

    } catch (error) {
      console.error('Error generating task:', error);
      setError(`Failed to generate task ${taskId}: ${error.message}`);
      setGenerationResults(prev => ({
        ...prev,
        [taskId]: {
          status: 'failed',
          error: error.message,
          timestamp: new Date()
        }
      }));
    } finally {
      setGeneratingTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  };

  // Check if user has access to a section based on role
  const hasAccessToSection = (section) => {
    // Admin has access to all sections
    if (user?.systemRole === 'admin') return true;

    // Check if section has required roles and if user's role is included
    if (section.requiredRoles && section.requiredRoles.length > 0) {
      return section.requiredRoles.includes(user?.systemRole || 'user');
    }

    // If no required roles specified, default to accessible
    return true;
  };

  // Filter sections based on user role access and application type
  const accessibleSections = sections.filter(section => {
    // First check user role access
    const hasAccess = showAllSections || section.isActive && hasAccessToSection(section);
    if (!hasAccess) return false;

    // Then filter based on application type
    if (applicationType === 'agentic-crew') {
      // For agentic crew projects, only show agentic sections
      // Check multiple fields for agentic identification
      const isAgenticSection = Boolean(
        (section.unique_name && section.unique_name.startsWith('agentic-')) ||
        (section.name && section.name.toLowerCase().includes('agentic')) ||
        (section.description && section.description.toLowerCase().includes('agentic')) ||
        (section.category && section.category.toLowerCase().includes('agentic'))
      );
      
      console.log(`Agentic crew filtering - Section: ${section.name}, isAgentic: ${isAgenticSection}`, section);
      return isAgenticSection;
    } else {
      // For other project types, exclude agentic sections
      const isAgenticSection = Boolean(
        (section.unique_name && section.unique_name.startsWith('agentic-')) ||
        (section.name && section.name.toLowerCase().includes('agentic')) ||
        (section.description && section.description.toLowerCase().includes('agentic')) ||
        (section.category && section.category.toLowerCase().includes('agentic'))
      );
      
      console.log(`Standard filtering - Section: ${section.name}, isAgentic: ${isAgenticSection}, excluding: ${isAgenticSection}`, section);
      return !isAgenticSection;
    }
  });

  // Function to filter out tasks that are already selected in project
  const filterAlreadySelectedTasks = (section, alreadySelectedTasks = []) => {
    if (!section.tasks) return section;

    // Clone the section to avoid mutating the original
    const filteredSection = { ...section };

    // Filter out tasks that are already in the project (if we're in selection mode)
    if (mode === 'selection') {
      if (alreadySelectedTasks.length > 0) {
        // Create a Set for more efficient lookups
        const alreadySelectedTasksSet = new Set(alreadySelectedTasks);

        // For already selected tasks, we completely filter them out from the UI
        // so they don't appear at all in the task selector
        filteredSection.tasks = section.tasks.filter(task => {
          const taskId = task.id || task._id || task.title;
          const isAlreadySelected = alreadySelectedTasksSet.has(taskId);
          return !isAlreadySelected;
        });
      }
    }

    return filteredSection;
  };

  // Check if we have valid sections, otherwise use fallback
  const noValidSections = !accessibleSections || accessibleSections.length === 0 ||
    accessibleSections.every(s => !s.tasks || s.tasks.length === 0);

  // Don't create fallback sections, only use what comes from API
  const fallbackSections = accessibleSections;

  // If there are no sections from the API, show an empty state
  if (noValidSections) {
    console.log('No valid sections found from API. Not using fallback hardcoded sections.');
  }

  // Apply filtering to remove tasks that are already in the project (but not in current selection)
  const filteredSections = mode === 'selection' ?
    fallbackSections.map(section => filterAlreadySelectedTasks(section, existingProjectTasks))
    : fallbackSections;

  // Calculate visible tasks and pass back to parent
  useEffect(() => {
    if (onVisibleTasksChange && filteredSections) {
      const visibleTaskIds = [];
      filteredSections.forEach(section => {
        if (section.tasks) {
          section.tasks.forEach(task => {
            const taskId = task.id || task._id || task.title;
            if (taskId) {
              visibleTaskIds.push(taskId);
            }
          });
        }
      });
      onVisibleTasksChange(visibleTaskIds);
    }
  }, [filteredSections, onVisibleTasksChange]);

  // Set first section as active if none is selected
  useEffect(() => {
    if (filteredSections && filteredSections.length > 0 && !activeSection) {
      setActiveSection(filteredSections[0]._id);
    }
  }, [filteredSections, activeSection]);

  // Note: allTaskIds is now passed as a prop from parent component


  // Loading state UI
  if (isLoadingSections || loading) {
    return (
      <Box sx={{ py: 5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading available tasks...
        </Typography>
      </Box>
    );
  }

  // Error state UI
  if (error || sectionsError) {
    return (
      <Alert severity="error" sx={{ my: 3 }}>
        {(error || sectionsError)?.message || 'Failed to load tasks. Please try again.'}
      </Alert>
    );
  }

  // If no sections available from API
  if (fallbackSections.length === 0 || noValidSections) {
    return (
      <Alert severity="info" sx={{ my: 3 }}>
        No sections or tasks are available from the API. Please make sure the API is working correctly.
      </Alert>
    );
  }

  // Mobile view - stacked layout
  if (isMobile) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {title && (
          <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="h2">
              {title}
            </Typography>
          </Box>
        )}

        {/* Main container with flexbox layout */}
        <Box
          sx={{
            width: '100%',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0 // This is important for nested flex containers
          }}
        >
          {/* Scrollable content area */}
          <Box
            sx={{
              width: '100%',
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              // Custom scrollbar styling
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                marginTop: '5px',
                marginBottom: '5px',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.4)',
                },
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: 'transparent',
              },
              pr: 3,
            }}
          >
            {/* All sections with horizontal dividers - mobile view */}
            <Box sx={{ width: '100%' }}>
              {filteredSections
                .map((section, index, filteredArray) => (
                  <React.Fragment key={section._id}>
                    {/* Space automatically handled by dotted divider */}

                    {/* Section as a row with name on left and tasks on right */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2
                      }}
                    >
                      {/* Section header with vertical divider on right */}
                      <Box sx={{ position: 'relative', pr: 3, width: '150px', ml: 3 }}>
                        {/* Vertical divider line on right */}
                        <Box
                          sx={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: index === fallbackSections.length - 1 ? 0 : '20px', // Stop before dotted line for all sections except last
                            width: '1px',
                            bgcolor: 'rgba(0, 0, 0, 0.12)'
                          }}
                        />

                        <Typography
                          variant="h5"
                          component="h2"
                          sx={{
                            fontSize: { xs: '1.1rem', md: '1.2rem' },
                            fontWeight: 600,
                            color: '#333',
                            pb: 0.5
                          }}
                        >
                          {section.name}
                        </Typography>

                        {/* Orchestrator selector - Enable avatar selection with projectId */}
                        <SectionOrchestratorSelector
                          sectionId={section._id}
                          sectionName={section.name}
                          projectId={projectId}
                          allowEdit={true}
                        />
                      </Box>

                      {/* Tasks grid */}
                      <Box sx={{ pl: 3 }}>
                        {/* Show "No topics to select" message if section has no tasks */}
                        {(!section.tasks || section.tasks.length === 0) ? (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'center',
                              minHeight: '150px',
                              py: 4
                            }}
                          >
                            <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                              No topics to select
                            </Typography>
                          </Box>
                        ) : (
                          <Grid container spacing={5}>
                            {section.tasks?.map((task) => {
                              // Debug log to see task structure
                              console.log('Mobile Task object:', task);
                              console.log('Mobile Task keys:', Object.keys(task));
                              
                              // Use the correct ID field - try id, _id, or title as fallback
                              const taskId = task.id || task._id || task.title;
                              console.log('Mobile Resolved taskId:', taskId);
                              
                              // Check if task is selected
                              const isSelected = selectedTasks.includes(taskId);

                              // When task is selected, always use 'selected' status for colors
                              const taskColors = isSelected
                                ? sectionColorService.getStatusColors('selected')
                                : sectionColorService.getTaskColors({
                                  task,
                                  section,
                                  sectionId: section._id,
                                  sectionName: section.name
                                });

                              return (
                                <Grid item xs={12} sm={6} md={3} key={taskId}>
                                  <Box
                                    sx={{
                                      height: 140,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      borderRadius: 2,
                                      overflow: 'hidden',
                                      position: 'relative',
                                      backgroundColor: taskColors.backgroundColor,
                                      cursor: mode === 'selection' ? 'pointer' : 'default',
                                      transition: 'all 0.2s ease',
                                      boxShadow: selectedTasks.includes(taskId)
                                        ? '0 4px 10px rgba(244, 196, 67, 0.15)' // Selected shadow
                                        : '0 1px 2px rgba(0,0,0,0.03)', // Default shadow
                                      '&:hover': mode === 'selection' ? {
                                        transform: 'translateY(-2px)',
                                        boxShadow: selectedTasks.includes(taskId)
                                          ? '0 6px 14px rgba(244, 196, 67, 0.2)'
                                          : '0 3px 8px rgba(0,0,0,0.06)'
                                      } : {}
                                    }}
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      if (mode === 'selection') {
                                        handleTaskSelection(taskId);
                                      }
                                    }}
                                  >
                                    <Box
                                      className="section-task-card"
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        width: '100%',
                                        p: 2,
                                        textAlign: 'center'
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'center',
                                          alignItems: 'center',
                                          width: 50,
                                          height: 50,
                                          borderRadius: '50%',
                                          backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                          mb: 1.5,
                                          transition: 'all 0.2s'
                                        }}
                                      >
                                        {React.cloneElement(getIconComponent(task.icon), {
                                          style: {
                                            fontSize: '1.7rem',
                                            color: taskColors.iconColor
                                          }
                                        })}
                                      </Box>

                                      <Typography
                                        variant="body1"
                                        component="h2"
                                        sx={{
                                          fontSize: '1rem',
                                          fontWeight: 500,
                                          color: '#333333',
                                          lineHeight: 1.3
                                        }}
                                      >
                                        {task.title}
                                      </Typography>
                                    </Box>

                                    {/* Selection indicator */}
                                    {selectedTasks.includes(taskId) && (
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          top: 8,
                                          right: 8,
                                          zIndex: 2,
                                          width: 20,
                                          height: 20,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center'
                                        }}
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 28.027 28.027">
                                          <path d="M28.027,14.014l-.943,1.722.469,1.906L26.2,19.059l-.044,1.963-1.68,1.017-.55,1.885-1.884.55-1.017,1.679-1.963.044-1.418,1.358-1.906-.469-1.722.943-1.721-.943-1.906.469L8.968,26.2l-1.963-.044-1.017-1.68L4.1,23.923l-.55-1.884-1.68-1.017-.044-1.963L.474,17.641l.469-1.906L0,14.014l.943-1.721L.474,10.386,1.832,8.968l.044-1.963,1.68-1.017L4.1,4.1l1.885-.55,1.016-1.68,1.963-.044L10.386.474l1.906.469L14.014,0l1.722.943L17.641.474l1.418,1.358,1.963.044,1.017,1.68,1.885.55.55,1.885,1.679,1.016L26.2,8.968l1.358,1.418-.469,1.906Z" fill="#62c148" />
                                          <path d="M289.508,319.711l-3.968-3.968,1.318-1.318,2.52,2.52,7.586-9.166,1.436,1.189Z" transform="translate(-277.955 -299.731)" fill="#fff" />
                                        </svg>
                                      </Box>
                                    )}

                                    {/* Generate button for code-aware tasks */}
                                    {projectId && isWorkspaceValid && mode === 'display' && (
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          top: 8,
                                          left: 8,
                                          zIndex: 2
                                        }}
                                      >
                                        <Tooltip title={generatingTasks.has(taskId) ? 'Generating...' : 'Generate with AI'}>
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleGenerateTask(taskId);
                                            }}
                                            disabled={generatingTasks.has(taskId)}
                                            sx={{
                                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                              '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 1)',
                                              },
                                              width: 32,
                                              height: 32
                                            }}
                                          >
                                            {generatingTasks.has(taskId) ? (
                                              <CircularProgress size={16} />
                                            ) : (
                                              <AutoFixHighIcon 
                                                sx={{ 
                                                  fontSize: 16, 
                                                  color: generationResults[taskId]?.status === 'completed' ? '#4caf50' : '#1976d2' 
                                                }} 
                                              />
                                            )}
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    )}

                                  </Box>
                                </Grid>
                              );
                            })}
                          </Grid>
                        )}
                      </Box>
                    </Box>

                    {/* Add dotted line under the tasks - matching Foundation component style */}
                    {index !== filteredArray.length - 1 && (
                      <div className="section-divider" />
                    )}
                  </React.Fragment>
                ))}

              {/* Add padding at the bottom to ensure scrolling works properly and content isn't hidden behind buttons */}
              <Box sx={{ height: '80px' }} />
            </Box>
          </Box>

          {/* Fixed button area at the bottom */}
          {mode === 'selection' && (showSaveButton || showContinueButton) && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              pt: 3,
              backgroundColor: 'white',
              position: 'sticky',
              bottom: 0,
              width: '100%',
              zIndex: 10,
              pr: 3
            }}>
              {showSkipButton && (
                <Button
                  variant="outlined"
                  onClick={handleSkip}
                  sx={{
                    color: 'text.secondary',
                    borderColor: 'rgba(0, 0, 0, 0.15)',
                    '&:hover': {
                      borderColor: 'rgba(0, 0, 0, 0.3)',
                      bgcolor: 'rgba(0, 0, 0, 0.02)',
                    },
                    borderRadius: '8px',
                    px: 3,
                    py: 1.2,
                    textTransform: 'none',
                    fontSize: '14px'
                  }}
                >
                  Skip
                </Button>
              )}

              {showContinueButton && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleContinue}
                  disabled={selectedTasks.length === 0 || continueLoading || loading}
                  sx={{
                    bgcolor: 'var(--color-blue)',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'var(--color-blue-dark)',
                    },
                    borderRadius: '8px',
                    px: 4,
                    py: 1.2,
                    textTransform: 'none',
                    fontSize: '15px',
                    fontWeight: 500,
                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
                    minWidth: 240,
                    ml: 2
                  }}
                  startIcon={<GitHubIcon />}
                >
                  {continueLoading ?
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CircularProgress size={16} sx={{ color: 'white', mr: 1.5 }} /> Saving tasks...
                    </Box> :
                    continueButtonText
                  }
                </Button>
              )}

              {showSaveButton && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  disabled={selectedTasks.length === 0 || loading}
                  sx={{
                    bgcolor: 'var(--color-blue)',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'var(--color-blue-dark)',
                    },
                    borderRadius: '8px',
                    px: 4,
                    py: 1.2,
                    textTransform: 'none',
                    fontSize: '15px',
                    fontWeight: 500,
                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
                    minWidth: 240,
                    ml: showContinueButton ? 2 : 0
                  }}
                >
                  {loading ?
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CircularProgress size={16} sx={{ color: 'white', mr: 1.5 }} /> Activating...
                    </Box> :
                    selectedTasks.length > 0
                      ? `${saveButtonText} (${selectedTasks.length})`
                      : saveButtonText
                  }
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // Desktop view - side-by-side layout
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {title && (
        <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2" sx={{ mr: 4 }}>
            {title}
          </Typography>
        </Box>
      )}

      {/* Main content container with flexbox layout */}
      <Box
        sx={{
          width: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0 // This is important for nested flex containers
        }}
      >
        {/* Scrollable content area */}
        <Box
          sx={{
            width: '100%',
            pr: 3,
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            // Custom scrollbar styling
            '&::-webkit-scrollbar': {
              width: '8px',
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-track': {
              borderRadius: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              marginTop: '5px',
              marginBottom: '5px',
            },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.4)',
              },
            },
            '&::-webkit-scrollbar-corner': {
              backgroundColor: 'transparent',
            },
          }}
        >
          {/* Display all sections with horizontal dividers */}
          <Box sx={{ width: '100%' }}>
            {filteredSections
              .map((section, index, filteredArray) => (
                <React.Fragment key={section._id}>
                  {/* Space automatically handled by dotted divider */}

                  {/* Section as a row with name on left and tasks on right */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      gap: { xs: 2, md: 4 }
                    }}
                  >
                    {/* Section header left aligned with vertical divider on right */}
                    <Box
                      sx={{
                        width: { xs: '100%', md: '150px' },
                        minWidth: { md: '150px' },
                        pt: { md: 1 },
                        position: 'relative',
                        ml: { md: 3 } // Add left margin to align with dialog title
                      }}
                    >
                      {/* Vertical divider line on right */}
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: index === fallbackSections.length - 1 ? 0 : '20px', // Stop before dotted line for all sections except last
                          width: '1px',
                          bgcolor: 'rgba(0, 0, 0, 0.12)',
                          display: { xs: 'none', md: 'block' }
                        }}
                      />

                      <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                          fontSize: { xs: '1.1rem', md: '1.2rem' },
                          fontWeight: 600,
                          color: '#333',
                          pb: 0.5,
                          pr: { md: 3 }
                        }}
                      >
                        {section.name}
                      </Typography>

                      {/* Orchestrator selector - Enable avatar selection with projectId */}
                      <SectionOrchestratorSelector
                        sectionId={section._id}
                        sectionName={section.name}
                        projectId={projectId}
                        allowEdit={true}
                      />
                    </Box>

                    {/* Tasks grid on the right */}
                    <Box sx={{ flex: 1, pl: { md: 2 } }}>
                      {/* Show "No topics to select" message if section has no tasks */}
                      {(!section.tasks || section.tasks.length === 0) ? (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'left',
                            alignItems: 'center',
                            minHeight: '150px',
                            py: 4
                          }}
                        >
                          <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                            No topics to select
                          </Typography>
                        </Box>
                      ) : (
                        <Grid container spacing={5}>
                          {section.tasks?.map((task, taskIndex) => {
                            // Debug log to see task structure
                            console.log('Task object:', task);
                            console.log('Task keys:', Object.keys(task));
                            
                            // Use the correct ID field - try id, _id, or title as fallback
                            const taskId = task.id || task._id || task.title;
                            console.log('Resolved taskId:', taskId);
                            
                            // Check if task is selected
                            const isSelected = selectedTasks.includes(taskId);

                            // When task is selected, always use 'selected' status for colors
                            const taskColors = isSelected
                              ? sectionColorService.getStatusColors('selected')
                              : sectionColorService.getTaskColors({
                                task,
                                section,
                                sectionId: section._id,
                                sectionName: section.name
                              });

                            return (
                              <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={taskId}>
                                <Box
                                  className="section-task-card"
                                  sx={{
                                    // Only display if there are tasks in this section
                                    display: 'flex',
                                    height: 140,
                                    width: '100%',
                                    maxWidth: { xs: '180px', md: '170px', lg: '160px', xl: '160px' },
                                    flexDirection: 'column',
                                    borderRadius: 0, // Remove rounded corners
                                    overflow: 'hidden',
                                    position: 'relative',
                                    backgroundColor: taskColors.backgroundColor,
                                    cursor: mode === 'selection' ? 'pointer' : 'default',
                                    transition: 'all 0.2s ease',
                                    ml: 'auto',
                                    mr: 'auto',
                                    mb: 1,
                                    border: 'none',
                                    boxShadow: selectedTasks.includes(taskId)
                                      ? '0 4px 10px rgba(244, 196, 67, 0.15)' // Selected shadow
                                      : 'none',
                                    '&:hover': mode === 'selection' ? {
                                      transform: 'translateY(-3px)',
                                      boxShadow: selectedTasks.includes(taskId)
                                        ? '0 6px 14px rgba(244, 196, 67, 0.2)'
                                        : '0 4px 8px rgba(0,0,0,0.08)'
                                    } : {}
                                  }}
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    if (mode === 'selection') {
                                      handleTaskSelection(taskId);
                                    }
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      height: '100%',
                                      width: '100%',
                                      pt: 4,
                                      pb: 3.5,
                                      px: 1.5,
                                      textAlign: 'center'
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        mb: 2,
                                        height: '60px',
                                        width: '60px',
                                        borderRadius: '50%',
                                        backgroundColor: 'rgba(255,255,255,0.6)'
                                      }}
                                    >
                                      {React.cloneElement(getIconComponent(task.icon), {
                                        style: {
                                          fontSize: '2.2rem',
                                          color: taskColors.iconColor
                                        }
                                      })}
                                    </Box>

                                    <Typography
                                      variant="body1"
                                      component="h2"
                                      sx={{
                                        fontSize: '1rem',
                                        fontWeight: 500,
                                        color: '#475969',
                                        lineHeight: 1.3,
                                        mt: 1
                                      }}
                                    >
                                      {task.title}
                                    </Typography>
                                  </Box>
                                  {/* Selection indicator */}
                                  {selectedTasks.includes(taskId) && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 10,
                                        right: 10,
                                        zIndex: 2
                                      }}
                                    >
                                      <svg id="Tick" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 28.027 28.027">
                                        <path id="Path_58116" data-name="Path_58116" d="M28.027,14.014l-.943,1.722.469,1.906L26.2,19.059l-.044,1.963-1.68,1.017-.55,1.885-1.884.55-1.017,1.679-1.963.044-1.418,1.358-1.906-.469-1.722.943-1.721-.943-1.906.469L8.968,26.2l-1.963-.044-1.017-1.68L4.1,23.923l-.55-1.884-1.68-1.017-.044-1.963L.474,17.641l.469-1.906L0,14.014l.943-1.721L.474,10.386,1.832,8.968l.044-1.963,1.68-1.017L4.1,4.1l1.885-.55,1.016-1.68,1.963-.044L10.386.474l1.906.469L14.014,0l1.722.943L17.641.474l1.418,1.358,1.963.044,1.017,1.68,1.885.55.55,1.885,1.679,1.016L26.2,8.968l1.358,1.418-.469,1.906Z" fill="#62c148"></path>
                                        <path id="Path_58117" data-name="Path_58117" d="M289.508,319.711l-3.968-3.968,1.318-1.318,2.52,2.52,7.586-9.166,1.436,1.189Z" transform="translate(-277.955 -299.731)" fill="#fff"></path>
                                      </svg>
                                    </Box>
                                  )}

                                  {/* Generate button for code-aware tasks */}
                                  {projectId && isWorkspaceValid && mode === 'display' && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 10,
                                        left: 10,
                                        zIndex: 2
                                      }}
                                    >
                                      <Tooltip title={generatingTasks.has(taskId) ? 'Generating...' : 'Generate with AI'}>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleGenerateTask(taskId);
                                          }}
                                          disabled={generatingTasks.has(taskId)}
                                          sx={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            '&:hover': {
                                              backgroundColor: 'rgba(255, 255, 255, 1)',
                                            },
                                            width: 32,
                                            height: 32
                                          }}
                                        >
                                          {generatingTasks.has(taskId) ? (
                                            <CircularProgress size={16} />
                                          ) : (
                                            <AutoFixHighIcon 
                                              sx={{ 
                                                fontSize: 16, 
                                                color: generationResults[taskId]?.status === 'completed' ? '#4caf50' : '#1976d2' 
                                              }} 
                                            />
                                          )}
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  )}

                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      )}
                    </Box>
                  </Box>

                  {/* Add dotted line under the tasks - matching Foundation component style */}
                  {index !== filteredArray.length - 1 && (
                    <div className="section-divider" />
                  )}
                </React.Fragment>
              ))}

            {/* Add padding at the bottom to ensure scrolling works properly and content isn't hidden behind buttons */}
            <Box sx={{ height: '80px' }} />
          </Box>
        </Box>

        {/* Fixed button area at the bottom */}
        {mode === 'selection' && (showSaveButton || showContinueButton) && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            pt: 3,
            backgroundColor: 'white',
            position: 'sticky',
            bottom: 0,
            width: '100%',
            zIndex: 10,
            pr: 3
          }}>
            {showSkipButton && (
              <Button
                variant="outlined"
                onClick={handleSkip}
                sx={{
                  color: 'text.secondary',
                  borderColor: 'rgba(0, 0, 0, 0.15)',
                  '&:hover': {
                    borderColor: 'rgba(0, 0, 0, 0.3)',
                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                  },
                  borderRadius: '8px',
                  px: 3,
                  py: 1.2,
                  textTransform: 'none',
                  fontSize: '14px',
                  mr: 2
                }}
              >
                Skip
              </Button>
            )}

            {showContinueButton && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleContinue}
                disabled={selectedTasks.length === 0 || continueLoading || loading}
                sx={{
                  bgcolor: 'var(--color-blue)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'var(--color-blue-dark)',
                  },
                  borderRadius: '8px',
                  px: 4,
                  py: 1.2,
                  textTransform: 'none',
                  fontSize: '15px',
                  fontWeight: 500,
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
                  minWidth: 240,
                  mr: showSaveButton ? 2 : 0
                }}
                startIcon={<GitHubIcon />}
              >
                {continueLoading ?
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={16} sx={{ color: 'white', mr: 1.5 }} /> Saving tasks...
                  </Box> :
                  continueButtonText
                }
              </Button>
            )}

            {showSaveButton && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={selectedTasks.length === 0 || loading}
                sx={{
                  bgcolor: 'var(--color-blue)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'var(--color-blue-dark)',
                  },
                  borderRadius: '8px',
                  px: 4,
                  py: 1.2,
                  textTransform: 'none',
                  fontSize: '15px',
                  fontWeight: 500,
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
                  minWidth: 240
                }}
              >
                {loading ?
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={16} sx={{ color: 'white', mr: 1.5 }} /> Activating...
                  </Box> :
                  selectedTasks.length > 0
                    ? `${saveButtonText} (${selectedTasks.length})`
                    : saveButtonText
                }
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SectionedTaskSelectorImproved;